import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AdminMeResponse } from '@shared/api-types';
import { ApiError, adminApiFetch } from './adminApiClient';
import { AdminAuthContext, type AdminAuthState } from './adminAuth';

interface Props {
  children: ReactNode;
  /** Test seam — start in this state without firing the /api/admin/me probe. */
  initialState?: AdminAuthState;
}

export function AdminAuthProvider({ children, initialState }: Props) {
  const [state, setState] = useState<AdminAuthState>(initialState ?? { status: 'unknown' });

  const refresh = useCallback(async () => {
    try {
      const admin = await adminApiFetch<AdminMeResponse>('/api/admin/me');
      setState({ status: 'authenticated', admin });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) {
        setState({ status: 'anonymous' });
        return;
      }
      setState({ status: 'anonymous' });
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await adminApiFetch<void>('/api/admin/auth/logout', { method: 'POST' });
    } catch {
      // best-effort
    }
    setState({ status: 'anonymous' });
  }, []);

  useEffect(() => {
    if (initialState) return;
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (!cancelled) void refresh();
    });
    return () => { cancelled = true; };
  }, [initialState, refresh]);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
